<script setup lang="ts">
import { Locale } from '~i18n/i18nConfig';

// Cookiebot implementation reference
// https://support.cookiebot.com/hc/en-us/articles/360003798814-Changing-or-withdrawing-consent

// COMPOSABLES
const { tt } = useTypedI18n();
const config = useRuntimeConfig();
const gtmEnabled = !config.public.googleTagManager.disabled;
const { locale } = useI18n();

// DATA
const { footerExploreLinks, footerLegalLinks } = await useStoryblokGlobalConfig();
const partners = computed(() => [
    ...(locale.value === Locale.IT
        ? [
              { src: '/partners/ANAV.webp', alt: 'Associazione Nazionale Autotrasporto Viaggiatori', class: 'bg-white p-0.5' },
              { src: '/partners/ANBTI.webp', alt: 'Associazione Nazionale Bus Turistici Italiani' },
          ]
        : []),
    ...(locale.value === Locale.DE ? [{ src: '/partners/NWO.webp', alt: 'NWO', class: 'bg-white p-0.5' }] : []),
    ...(locale.value === Locale.PL
        ? [
              { src: '/partners/IGKM.webp', alt: 'IGKM' },
              { src: '/partners/OSPO.webp', alt: 'Ogólnopolskie Stowarzyszenie Przewoźników Osobowych' },
              { src: '/partners/SOPTZ.webp', alt: 'SOPTZ' },
          ]
        : []),
    { src: '/partners/GPN.webp', alt: 'Global Passenger Network' },
]);
const navMenuUi = {
    link: 'text-inverted font-normal hover:text-inverted hover:underline inline text-xs hover:underline md:text-sm',
    list: 'space-y-3',
};

// HANDLERS
const top = (): void => scrollToTop();
</script>

<template>
    <footer class="bg-primary-950 text-inverted flex w-full justify-center">
        <BaseWidthContainer class="flex flex-col gap-8 py-8 md:gap-9 md:py-12 xl:py-16">
            <div class="flex flex-col gap-8">
                <div class="grid grid-cols-2 gap-x-5 gap-y-8 md:grid-cols-3 md:gap-4 xl:gap-8">
                    <div class="col-span-2 grid grid-cols-2 gap-x-5 gap-y-4 md:col-span-1 md:flex md:flex-col md:gap-4">
                        <div class="col-span-2">
                            <SvgoFleequidLogo class="h-6" :font-controlled="false" />
                        </div>
                        <FleequidFooterContacts />
                        <div>
                            <img
                                :src="`/next-gen-eu/${locale}.webp`"
                                :alt="tt('footer.nextGenEU')"
                                class="h-12 rounded-sm object-scale-down md:object-cover"
                            />
                        </div>
                    </div>
                    <nav class="flex flex-col gap-3">
                        <h4 class="text-base font-semibold md:text-lg">{{ tt('footer.explore') }}</h4>
                        <UNavigationMenu orientation="vertical" :items="footerExploreLinks" variant="link" :ui="navMenuUi" />
                    </nav>
                    <nav class="flex flex-col gap-3">
                        <h4 class="text-base font-semibold md:text-lg">{{ tt('footer.legal') }}</h4>
                        <UNavigationMenu orientation="vertical" :items="footerLegalLinks" variant="link" :ui="navMenuUi" />

                        <template v-if="gtmEnabled">
                            <a class="pl-3 text-xs hover:underline md:text-sm" href="javascript:Cookiebot.renew()">
                                {{ tt('footer.cookiePolicy.changeConsent') }}
                            </a>
                            <a class="pl-3 text-xs hover:underline md:text-sm" href="javascript:Cookiebot.withdraw()">
                                {{ tt('footer.cookiePolicy.revokeConsent') }}
                            </a>
                        </template>
                    </nav>
                </div>
            </div>
            <USeparator class="opacity-20" />
            <div class="flex w-full flex-row flex-wrap justify-around gap-4">
                <p class="text-xl">{{ tt('footer.partners') }}</p>
                <div class="flex flex-row flex-wrap justify-center gap-4">
                    <template v-for="item in partners" :key="item.src">
                        <div>
                            <img :src="item.src" :alt="item.alt" :class="item.class" class="h-16 rounded-sm object-scale-down md:object-cover" />
                        </div>
                    </template>
                </div>
            </div>
            <USeparator class="opacity-20" />
            <div class="flex items-start justify-between sm:items-center">
                <div class="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                    <p class="text-xs text-neutral-400"><FleequidCopyright /></p>
                </div>
                <UButton variant="link" color="neutral" size="xs" class="text-inverted hover:text-inverted active:text-inverted" @click="top">{{
                    tt('footer.backTop')
                }}</UButton>
            </div>

            <USeparator class="opacity-20" />

            <div class="flex flex-col gap-3 xl:flex-row xl:justify-between">
                <p class="text-xs text-neutral-400">{{ tt('gateway.stripeTitle') }} {{ tt('gateway.stripeDescription') }}</p>
                <div class="flex flex-wrap justify-start gap-3">
                    <UIcon name="pmt:pay-pal-logo" class="h-7 w-10" />
                    <UIcon name="pmt:google-pay" class="h-7 w-10" />
                    <UIcon name="pmt:apple-pay" class="h-7 w-10" />
                    <UIcon name="pmt:card-visa" class="h-7 w-10" />
                    <UIcon name="pmt:card-mastercard" class="h-7 w-10" :font-controlled="false" filled />
                    <UIcon name="pmt:card-amex" class="h-7 w-10" :font-controlled="false" filled />
                    <SvgoPoweredByStripe class="h-7 max-h-7 min-h-7" :font-controlled="false" filled />
                </div>
            </div>
        </BaseWidthContainer>
    </footer>
</template>
